import AsyncStorage from '@react-native-async-storage/async-storage';
import { isAuthRetryableFetchError, type Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AppState } from 'react-native';

import { consumeRecoverySession } from './recovery';
import { Sentry } from './sentry';
import { AUTH_STORAGE_KEY, supabase } from './supabase';

/**
 * Whether the persisted session is KNOWN yet.
 *
 * `error` is deliberately its own state rather than a null session, and this is the reason
 * every consumer holds instead of falling through: a failed load means the session is
 * UNKNOWN, not ABSENT, and nothing may conflate the two. PowerSyncProvider reads a
 * known-null session as a sign-out and clears the local database — including ps_crud, the
 * un-uploaded write queue. A storage hiccup, or merely being offline, must not be able to
 * destroy a pilot's offline run-seal. Consumers act only on `ready`.
 */
export type AuthStatus = 'loading' | 'ready' | 'error';

type AuthState = {
  session: Session | null;
  status: AuthStatus;
  /** Re-attempt the persisted-session load after an error. */
  retry: () => void;
  /** Abandon the session. Escapes an `error` status even if supabase's own sign-out can't. */
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  session: null,
  status: 'loading',
  retry: () => {},
  signOut: async () => {},
});

/**
 * Drop supabase's persisted session straight out of storage.
 *
 * The last resort when `supabase.auth.signOut()` itself fails: its `_signOut` runs inside
 * `_useSession`, which reads the very session we can't read, so a store that rejects reads
 * takes the library's own sign-out down with it and never reaches the removal. Without
 * this, a "signed out" device still holds the unreadable token and every relaunch lands
 * back on the recovery screen.
 *
 * Takes `AUTH_STORAGE_KEY` and supabase's chunked `.0`/`.1`/… writes under it. We name that
 * key in `supabase.ts` rather than sniffing storage for it, so this deletes what we know we
 * wrote instead of whatever happens to look like a session.
 */
async function purgePersistedSession(): Promise<void> {
  const keys = await AsyncStorage.getAllKeys();
  const authKeys = keys.filter(
    (k) => k === AUTH_STORAGE_KEY || k.startsWith(`${AUTH_STORAGE_KEY}.`),
  );
  if (authKeys.length > 0) await AsyncStorage.multiRemove(authKeys);
}

/**
 * Read the session supabase persisted, without asking supabase.
 *
 * `getSession()` refuses to hand back an expired session it cannot refresh: offline it
 * resolves `{ session: null, error }` even though the session is still on disk (a retryable
 * refresh failure does not remove it). This app must keep working at a gate with no signal,
 * so on that path we read the persisted session ourselves and run on it until connectivity
 * returns and the auto-refresh takes over. Null on anything unreadable or shapeless — the
 * caller falls back to `error`, never to a guess.
 */
async function readPersistedSession(): Promise<Session | null> {
  try {
    const raw = await AsyncStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const persisted = JSON.parse(raw) as Session;
    // Enough shape to gate on (a user identity) and to recover with (a refresh token).
    if (!persisted?.user?.id || !persisted.refresh_token) return null;
    return persisted;
  } catch {
    return null;
  }
}

// Supabase only auto-refreshes the token while it is actively running. Tie that
// to app foreground/background so the session stays fresh on native.
AppState.addEventListener('change', (state) => {
  if (state === 'active') {
    void supabase.auth.startAutoRefresh();
  } else {
    void supabase.auth.stopAutoRefresh();
  }
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  // Not a count anyone reads — bumping it is how `retry()` re-runs the load effect.
  const [retryNonce, setRetryNonce] = useState(0);

  // Bumped by every auth event, to date the load below against.
  const authEpoch = useRef(0);

  useEffect(() => {
    const startedAt = authEpoch.current;
    let cancelled = false;
    // An auth event that lands mid-read supersedes it: the event carries the session with
    // it, so it is strictly newer knowledge than a read of storage that was already in
    // flight. Applying the read anyway would let it resurrect a session the user signed out
    // of while it was pending, or drag an event-resolved session back to `error`.
    const superseded = () => cancelled || authEpoch.current !== startedAt;

    const failed = (error: unknown) => {
      if (superseded()) return;
      setStatus('error');
      Sentry.captureException(error, {
        tags: { area: 'auth', outcome: 'session-load-failed' },
      });
    };

    supabase.auth
      .getSession()
      .then(async ({ data, error }) => {
        // A null session means SIGNED OUT only when `error` is null too. getSession()
        // reports a failure it could not recover from by RESOLVING with both set: once the
        // access token has expired it tries a refresh, and auth-js catches the offline
        // fetch failure and returns it rather than throwing. Take `data` alone and a pilot
        // who is merely offline is reported as signed out — and the sign-out path clears
        // the local database, un-uploaded writes and all.
        if (error) {
          // A RETRYABLE error means offline/unreachable — routine here, not a failure. The
          // session is still on disk (a retryable refresh failure does not remove it), so
          // run the app on it rather than fail-closing a pilot out of their own local data;
          // the auto-refresh replaces it (or a revoke signs it out) once connectivity
          // returns. Anything else, or nothing recoverable on disk, stays UNKNOWN.
          if (isAuthRetryableFetchError(error)) {
            const persisted = await readPersistedSession();
            if (superseded()) return;
            if (persisted) {
              setSession(persisted);
              setStatus('ready');
              return;
            }
          }
          return failed(error);
        }
        if (superseded()) return;
        setSession(data.session);
        setStatus('ready');
      })
      // And it REJECTS when the store itself is unreadable: the read is unguarded all the
      // way down (__loadSession is try/finally, getItemAsync only wraps the JSON.parse).
      // Unhandled, that left `status` on `loading` and every consumer on a blank screen.
      .catch(failed);

    return () => {
      cancelled = true;
    };
  }, [retryNonce]);

  // Subscribed once, and independently of the load above: an auth TRANSITION carries the
  // session with it, so it is definitive knowledge and resolves `status` even after the
  // load failed. That is what makes the error screen's "Sign out" work — `signOut()` emits
  // SIGNED_OUT, and if that did not release `status` the screen would have no way out.
  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      // INITIAL_SESSION is not a transition — it is an echo of the same `__loadSession` the
      // load above reads, minus the error discrimination: auth-js CATCHES a failed load and
      // emits the event with a null session (GoTrueClient `_emitInitialSession`), which is
      // the failed-load-as-sign-out flattening this provider exists to prevent.
      if (event === 'INITIAL_SESSION') return;
      authEpoch.current += 1;
      setSession(next);
      setStatus('ready');
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const retry = useCallback(() => {
    setStatus('loading');
    setRetryNonce((n) => n + 1);
  }, []);

  // Give up on the session locally, when supabase would not or could not. Reports, drops the
  // token so the next launch doesn't land straight back here, and resolves the state itself.
  const abandonSession = useCallback(async (error: unknown) => {
    // Resolving the state locally is as definitive as an auth event, and must date-stamp
    // itself the same way — otherwise a session load still in flight ("Try again", then
    // "Sign out") resolves afterwards and resurrects the session the user just abandoned.
    authEpoch.current += 1;
    // A password-recovery mark authorizes a password change on the session it was
    // established for, so it must not outlive it. supabase.ts drops the mark on SIGNED_OUT,
    // but this path exists precisely because no SIGNED_OUT is emitted here — leaving the
    // mark behind would let the next sign-in of the same user change the password on the
    // reset screen with no email round-trip and no current-password reauth.
    consumeRecoverySession();
    Sentry.captureException(error, {
      tags: { area: 'auth', outcome: 'sign-out-failed' },
    });
    try {
      await purgePersistedSession();
    } catch (purgeError) {
      // Best-effort: a store that rejects writes too leaves the token behind, and the
      // next launch returns to the recovery screen. Escaping THIS one still beats not.
      Sentry.captureException(purgeError, {
        tags: { area: 'auth', outcome: 'session-purge-failed' },
      });
    }
    setSession(null);
    setStatus('ready');
  }, []);

  // Owned here because supabase's own sign-out fails silently in the two states the recovery
  // screen is reached in, and neither emits SIGNED_OUT: it REJECTS on the same broken store
  // that caused the error status, and it RESOLVES WITH `{ error }` when the server revoke
  // fails — `_signOut` skips `_removeSession()` for anything that isn't a 401/403/404, and an
  // offline fetch failure is exactly that. Both paths abandon the session locally instead.
  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) await abandonSession(error);
    } catch (error) {
      await abandonSession(error);
    }
  }, [abandonSession]);

  const value = useMemo(
    () => ({ session, status, retry, signOut }),
    [session, status, retry, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
